import type { MouseEvent, ReactNode } from "react";
import { useWorkspace } from "./useWorkspace";
import type { AppPath, Placement } from "./workspaceTypes";

interface WorkspaceLinkProps {
  readonly route: AppPath;
  /** Where a plain click opens it; `active` replaces this tab, as any link does. */
  readonly placement: Placement;
  readonly className?: string;
  readonly title?: string;
  /** Its accessible name where its text alone doesn't say what it opens. */
  readonly ariaLabel?: string;
  readonly children: ReactNode;
}

/**
 * A link to a page of this app that a plain click opens in a chosen place, such as a result
 * beside its document (D-07). It is a real link, so Ctrl, Cmd and middle click open it in a new
 * tab and Alt+click beside, as every link does (F90 Q2, TR-B4-02); the workspace handles those
 * before this click.
 */
export default function WorkspaceLink({ route, placement, className, title, ariaLabel, children }: WorkspaceLinkProps) {
  const workspace = useWorkspace();
  const openPlain = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    workspace.open(route, { placement });
  };
  return (
    <a href={route} className={className} title={title} aria-label={ariaLabel} onClick={openPlain}>
      {children}
    </a>
  );
}
